import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import { Box, IconButton, Text } from "@chakra-ui/react-v3";
import { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { createBlobUrl } from "../shared-logic/base64-utils";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type SimplePdfViewerProps = {
  base64Content: string;
  maxHeight?: string;
};

/**
 * Simple PDF viewer component with zoom controls
 */
export function SimplePdfViewerV2({ base64Content, maxHeight = "600px" }: SimplePdfViewerProps) {
  const [blobUrl, setBlobUrl] = useState<string | undefined>(undefined);
  const [scale, setScale] = useState(1.0);
  const [numPages, setNumPages] = useState<number>(0);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const url = createBlobUrl(base64Content, "application/pdf");
    if (!url) {
      setError("Failed to create PDF URL");
      return;
    }
    setBlobUrl(url);

    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [base64Content]);

  if (error) {
    return <Box color="red.500">Failed to load PDF: {error}</Box>;
  }

  if (!blobUrl) {
    return <Box>Loading PDF...</Box>;
  }

  return (
    <Box width="100%">
      <Box display="flex" gap={2} mb={2} alignItems="center" justifyContent="end">
        <Text>{Math.round(scale * 100)}%</Text>
        <IconButton
          aria-label="Zoom out"
          onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
          size="2xs"
        >
          <MinusIcon />
        </IconButton>
        <IconButton
          aria-label="Zoom in"
          onClick={() => setScale(s => Math.min(3, s + 0.1))}
          size="2xs"
        >
          <AddIcon />
        </IconButton>
      </Box>
      <Box overflow="auto" maxHeight={maxHeight}>
        <Document
          file={blobUrl}
          loading={<Box>Loading PDF document...</Box>}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          onLoadError={error => {
            console.error("PDF load error:", error);
            setError("Unable to display PDF");
          }}
        >
          {Array.from({ length: numPages }, (_, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              scale={scale}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          ))}
        </Document>
      </Box>
    </Box>
  );
}
